import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bikeService } from "@/api/bikeService";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";

const formSchema = z.object({
  modelId: z.string().min(1, { message: "Please select a bike model" }),
  serialNumber: z.string().min(1, { message: "Serial number is required" }),
  batteryLevel: z.coerce.number().min(0).max(100).default(100),
  status: z.enum(["active", "inactive"]).default("active"),
  location: z.object({
    type: z.enum(["site", "home"]),
    id: z.string().optional(),
  }),
})
.refine(
  (data) => {
    if (data.location.type === "site") {
      return !!data.location.id;
    }
    if (data.location.type === "home") {
      return !!data.location.id;
    }
    return true;
  },
  {
    message: "Please select a user.",
    path: ["location", "id"],
  }
);

export function AddBikeForm({ onSuccess }: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isUserLocation, setIsUserLocation] = useState(false);

  // Form definition
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      modelId: "",
      serialNumber: "",
      batteryLevel: 100,
      status: "active",
      location: {
        type: "site",
        id: "",
      },
    },
  });

  // Mutations
  const createBikeMutation = useMutation({
    mutationFn: async (data: any) => {
      let assignedLocation = null;
      if (data.siteId && sites) {
        const selectedSite = sites.find(site => site.id === data.siteId);
        assignedLocation = selectedSite ? selectedSite.name : null;
      }
      
      const bike = await bikeService.createBike({
        modelId: data.modelId,
        serialNumber: data.serialNumber,
        name: `Bike ${data.serialNumber}`,
        assignedLocation,
        totalMileage: 0,
        batteryStatus: data.batteryLevel,
        maintenanceStatus: "ok",
        bikeStatus: data.status,
        userLocationId: data.userLocationId || null,
      });

      console.log("Bike created:", bike);
      return bike;
    },
    onSuccess: (bike) => {
      toast.success("Bike created!", {
        description: `Successfully created: ${bike.name}`,
      });
      queryClient.invalidateQueries({ queryKey: ["bikes"] });
      form.reset();
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      toast.error(`Error adding bike: ${error.message}`);
    },
  });

  // Queries
  const { data: bikeModels, isLoading: isLoadingModels } = useQuery({
    queryKey: ["bikeModels"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bike_models')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
  });

  const { data: sites, isLoading: isLoadingSites } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sites')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
  });

  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, username')
        .order('username', { ascending: true });
      
      if (error) throw error;
      return data || [];
    }
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    const currentUser = supabase.auth.getUser();
    
    createBikeMutation.mutate({
      ...values,
      // Set the appropriate location fields based on the location type
      siteId: values.location.type === "site" ? values.location.id : null,
      userLocationId: values.location.type === "home" ? values.location.id : null,
    });
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="modelId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bike Model</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a bike model" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {bikeModels?.map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          {model.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>Choose the model of the bike</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="serialNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Serial Number</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter bike serial number" {...field} />
                  </FormControl>
                  <FormDescription>
                    The unique serial number of the bike
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="batteryLevel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Initial Battery Level (%)</FormLabel>
                  <FormControl>
                    <Input type="number" min={0} max={100} {...field} />
                  </FormControl>
                  <FormDescription>
                    Set the initial battery level of the bike
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>Set the initial status of the bike</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center space-x-2">
              <Switch
                checked={isUserLocation}
                onCheckedChange={(checked) => {
                  setIsUserLocation(checked);
                  form.setValue("location.type", checked ? "home" : "site");
                  form.setValue("location.id", "");
                }}
              />
              <span>Assign to user's home location</span>
            </div>

            <FormField
              control={form.control}
              name="location.id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{isUserLocation ? "User" : "Site"}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={`Select ${isUserLocation ? "user" : "site"}`} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isUserLocation
                        ? users?.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.username}
                            </SelectItem>
                          ))
                        : sites?.map((site) => (
                            <SelectItem key={site.id} value={site.id}>
                              {site.name}
                            </SelectItem>
                          ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {isUserLocation
                      ? "Select the user who will keep the bike at home"
                      : "Select the site where the bike will be stationed"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full"
              disabled={!form.formState.isValid || createBikeMutation.isPending}
            >
              {createBikeMutation.isPending ? "Adding..." : "Add Bike"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

export default AddBikeForm;
